import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, IconButton, Stack, Tooltip } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import { useStyles } from '../styles/projectLinksStyles';
import AddRiskModal from './popups/AddRiskModal';
import { DeleteModal } from './popups';

import {
  getRisk,
 addProjectRisk,
 updateProjectRisk,
 deleteProjectRisk
} from '../../../../services/projectService';
import { useProject } from '../../Layout/header/context';


const ProjectRisks = ({ projectId }) => {
  const classes = useStyles();
  const { isEditPermission } = useProject();
  const [risks, setRisks] = useState([]);
  const [modal, setModal] = useState({ open: false, action: '' });
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [loading, setLoading] = useState(false);

  const user = useSelector((state) => state.login.user);

  const fetchRisks = useCallback(async () => {
    try {
      const res = await getRisk(projectId);
      setRisks(res?.data?.data || []);
    } catch (error) {
      toast.error("Failed to load risks");
    }
  }, [projectId]);

  useEffect(() => {
    fetchRisks();
  }, [fetchRisks]);

  const openAddModal = () => {
    setSelectedRisk(null);
    setModal({ open: true, action: "add" });
  };

  const openEditModal = (risk) => {
    setSelectedRisk(risk);
    setModal({ open: true, action: "edit" });
  };

  const openDeleteModal = (risk) => {
    setSelectedRisk(risk);
    setModal({ open: true, action: "delete" });
  };

  const closeModal = () => setModal({ open: false, action: "" });

  // ---------------------- ADD RISK ----------------------
  const handleAddRisk = async (data) => {
    try {
      setLoading(true);
      await addProjectRisk({
        title: data.riskName,
        projectId
      });
      toast.success("Risk added successfully");
      closeModal();
      fetchRisks();
    } catch (error) {
      toast.error("Failed to add risk");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- UPDATE RISK ----------------------
  const handleUpdateRisk = async (data) => {
    try {
      setLoading(true);
      await updateProjectRisk(selectedRisk.id, {
        title: data.riskName,
      });
      toast.success("Risk updated successfully");
      closeModal();
      fetchRisks();
    } catch (error) {
      toast.error("Failed to update risk");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- DELETE RISK ----------------------
  const handleDeleteRisk = async () => {
    try {
      setLoading(true);
      await deleteProjectRisk(selectedRisk.id);
      toast.success("Risk deleted");
      setRisks((prev) => prev.filter((r) => r.id !== selectedRisk.id));
      closeModal();
    } catch (error) {
      toast.error("Failed to delete risk");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- UI TILE (Same as Project Links) ----------------------
  const AddRiskTile = () => (
    <Box className={classes.linkButton} onClick={!isEditPermission ? null : openAddModal}>
      <Box className={classes.addIcon}>
        <Add />
      </Box>
      <Typography variant="body2" className={classes.buttonText}>
        Add Key Risk
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={2}>
      <Typography variant="h4" color="GrayText">Key Risks</Typography>

      <Stack direction="row" flexWrap="wrap" gap={2}>
        {isEditPermission && <AddRiskTile />}
        {!isEditPermission && risks.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No risks have been added yet.
          </Typography>
        )}

        {risks.map((risk) => (
          <Box key={risk.id} className={classes.linkButton} display="flex" alignItems="center">
            <Tooltip title={risk.title} arrow>
              <Typography className={classes.linkName}>{risk.title}</Typography>
            </Tooltip>

            <Box display="flex" ml="auto">
              {isEditPermission && (
                <>
                  <IconButton size="small" onClick={() => openEditModal(risk)}>
                    <Edit fontSize="small" className={classes.editButton} />
                  </IconButton>
                  <IconButton size="small" onClick={() => openDeleteModal(risk)}>
                    <Delete fontSize="small" className={classes.deleteButton}/>
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Add / Edit Modal */}
      <AddRiskModal
        open={modal.open && (modal.action === "add" || modal.action === "edit")}
        onClose={closeModal}
        risk={selectedRisk}
        onSubmit={modal.action === "add" ? handleAddRisk : handleUpdateRisk}
        isLoading={loading}
        projectId={projectId} 
      />

      {/* Delete Modal */}
      <DeleteModal
        open={modal.open && modal.action === "delete"}
        onClose={closeModal}
        onDelete={handleDeleteRisk}
        isLoading={loading}
      />
    </Stack>
  );
};

export default ProjectRisks;
